import React, { useEffect, useState, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useCart } from "../Context/Createcontext";
import axios from "axios";
import { Helmet } from "react-helmet";
import Popup from "../Components/common/Popup";
import { api_url } from "../config/config";
import addToCartIcon from "../Icons/addToCartIcon.svg";
import { toast } from "react-hot-toast";
import wish from "../images/wish.svg";
import wishlist from "../images/wishlist_nofill.svg";
import { useWishlist } from "../Context/WishlistContext";
import { useApp } from "../Context/AppContext";
import HeroBannner from "./HeroBannner";
import { normalizeSearchQuery } from "../utils/normalizeSearch";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import { toProductSlug } from "../utils/productSlug";

/* ─────────────────────────────────────────────────────────────────
   PRICE HELPER  (same logic as all other pages)
   variant.mrp (string|null)  → original price
   variant.discounted_price (number|null) → sale price
   • disc > 0 AND disc < mrp → strikethrough + disc + badge
   • otherwise               → show only mrp, no strikethrough
───────────────────────────────────────────────────────────────── */
const getPriceInfo = (variants, discountPercent) => {
  if (!variants?.length)
    return { mrp: null, finalPrice: null, showDiscount: false, pct: 0 };
  const sorted = [...variants]
    .map((v) => ({
      ...v,
      _mrp: parseFloat(v.mrp) || 0,
      _disc: parseFloat(v.discounted_price) || 0,
    }))
    .filter((v) => v._mrp > 0)
    .sort((a, b) => a._mrp - b._mrp);
  if (!sorted.length)
    return { mrp: null, finalPrice: null, showDiscount: false, pct: 0 };
  const best = sorted[0];
  const showDiscount = best._disc > 0 && best._disc < best._mrp;
  return {
    mrp: best._mrp,
    finalPrice: showDiscount ? best._disc : best._mrp,
    showDiscount,
    pct: showDiscount
      ? discountPercent ||
        Math.round(((best._mrp - best._disc) / best._mrp) * 100)
      : 0,
  };
};

const SORT_OPTIONS = [
  { value: "low-to-high", label: "Price: Low → High" },
  { value: "high-to-low", label: "Price: High → Low" },
  { value: "discount", label: "Best Discount" },
];

// Infinite scroll fetches products 20 at a time; each page comes back
// sorted on its own, but simply concatenating pages breaks the sort at
// every page boundary (page 2 restarts from its own low/high instead of
// continuing page 1's order). Re-sorting the full accumulated list after
// every append keeps it correctly ordered end to end regardless of how
// each page was sorted individually.
const sortProductsByOrder = (list, order) => {
  const decorated = list.map((p) => {
    const info = getPriceInfo(p.variant, p.discount_percent);
    return {
      product: p,
      price: info.finalPrice ?? Number.POSITIVE_INFINITY,
      pct: info.pct || 0,
    };
  });
  if (order === "high-to-low") decorated.sort((a, b) => b.price - a.price);
  else if (order === "discount") decorated.sort((a, b) => b.pct - a.pct);
  else decorated.sort((a, b) => a.price - b.price);
  return decorated.map((d) => d.product);
};

const SortDropdown = ({ sortOrder, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current =
    SORT_OPTIONS.find((o) => o.value === sortOrder) || SORT_OPTIONS[0];
  useEffect(() => {
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((p) => !p)}
        className="flex items-center gap-2 bg-white border border-[#c8d8d7] hover:border-[#5fbdb3] text-[#1e3330] text-[13px] font-semibold px-4 h-10 rounded-xl transition-all duration-200 font-poppins whitespace-nowrap"
      >
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="4" y1="6" x2="20" y2="6" />
          <line x1="8" y1="12" x2="16" y2="12" />
          <line x1="11" y1="18" x2="13" y2="18" />
        </svg>
        Sort: {current.label}
        <svg
          className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          width="11"
          height="11"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1.5 z-50 bg-white border border-[#c8d8d7] rounded-2xl shadow-[0_8px_32px_rgba(22,44,42,0.16)] overflow-hidden min-w-[200px]">
          {SORT_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
              className={`w-full flex items-center justify-between px-4 py-3 text-[13px] font-semibold text-left transition-all font-poppins
                ${sortOrder === opt.value ? "bg-[#ddf0ee] text-[#1e4a46]" : "text-[#1e3330] hover:bg-[#f2f8f7] hover:text-[#1e4a46]"}`}
            >
              {opt.label}
              {sortOrder === opt.value && (
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#1e4a46"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const CategoryIcon = () => (
  <svg
    width="40"
    height="40"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#5fbdb3"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 6h18M3 12h18M3 18h18" />
    <circle cx="3" cy="6" r="1" fill="#5fbdb3" />
    <circle cx="3" cy="12" r="1" fill="#5fbdb3" />
    <circle cx="3" cy="18" r="1" fill="#5fbdb3" />
  </svg>
);

/* ══════════════════════════════════════════════════════════════ */
const Categories = () => {
  const {
    handleAddToCart,
    showLoginPopup,
    setShowLoginPopup,
    handleRemoveFromCart,
    cartItems,
    setCartItems,
  } = useCart();
  // catid/setCatid are shared (via AppContext) with the Subnavbar dropdown —
  // clearing/selecting here updates the navbar dropdown too, and vice versa.
  const {
    contextCategories,
    selectedCategoryIds: catid,
    setSelectedCategoryIds: setCatid,
  } = useApp();
  const {
    wishlist: wishlistItems,
    addToWishlist,
    removeFromWishlist,
    loading: wishlistLoading,
  } = useWishlist();

  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const brandScrollRef = useRef(null);
  const searchInputRef = useRef(null);
  const loadMoreTriggerRef = useRef(null);

  /* ── State ── */
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [sortOrder, setSortOrder] = useState("low-to-high");
  const [tooltipOpen, setTooltipOpen] = useState(null);
  const [heroSearch, setHeroSearch] = useState("");
  const debouncedHeroSearch = useDebouncedValue(heroSearch, 400);
  const [brandSearch, setBrandSearch] = useState("");
  const [filteredBrands, setFilteredBrands] = useState([]);
  const [brandLoading, setBrandLoading] = useState(false);
  const [brandPage, setBrandPage] = useState(1);
  const [hasMoreBrands, setHasMoreBrands] = useState(true);
  const [selectedBrands, setSelectedBrands] = useState(new Set());
  const [manufactid, setManufactid] = useState([]);

  /* ── Selected category (from navigation state OR first category) ──
     category_ids/category_names (plural) come from the Subnavbar's
     multi-select dropdown; category_id/category_name (singular) come
     from single-category links elsewhere in the app (Home page grid,
     Subnavbar breadcrumb, etc). ── */
  const [selectedCategory, setSelectedCategory] = useState(
    location.state?.category_names?.join(", ") ||
      location.state?.category_name ||
      null,
  );

  /* ── Sync state if navigated with new category (single or multiple) —
     and reset to "all categories" when landing with no category state at
     all (e.g. the homepage "View All" link), so a selection left over from
     a previous visit doesn't leak into a fresh "show everything" visit. ── */
  useEffect(() => {
    if (location.state?.category_ids?.length) {
      setCatid(location.state.category_ids);
      setSelectedCategory(location.state.category_names?.join(", ") || null);
    } else if (location.state?.category_id) {
      setCatid([location.state.category_id]);
      setSelectedCategory(location.state.category_name);
    } else {
      setCatid([]);
      setSelectedCategory(null);
    }
  }, [location.state?.category_id, location.state?.category_ids]);

  /* ── Brands: server-side search + pagination.
     The app-wide contextBrands list is capped at 100 manufacturers, so
     filtering it client-side silently misses any brand beyond that cap —
     including ones that do have products in the selected category. ── */
  const fetchBrands = async (page = 1, search = "", append = false) => {
    if (brandLoading) return;
    setBrandLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 18 });
      if (search.trim()) params.append("search", search.trim());

      const response = await axios.get(
        `${api_url}/manufacturer?${params.toString()}`,
      );

      if (response.data?.results) {
        const formatted = response.data.results
          .filter((m) => m && m.manufacturer_id)
          .map((m) => ({
            id: m.manufacturer_id,
            name: m.manufacturer_name || m.name || "",
          }));

        setFilteredBrands((prev) =>
          append ? [...prev, ...formatted] : formatted,
        );
        setHasMoreBrands(page < (response.data.totalPages || 1));
      }
    } catch (err) {
      console.error("Error fetching brands:", err);
    } finally {
      setBrandLoading(false);
    }
  };

  /* ── Initial brand fetch ── */
  useEffect(() => {
    fetchBrands(1, "");
  }, []);

  /* ── Brand search with debounce ── */
  useEffect(() => {
    const timer = setTimeout(() => {
      setBrandPage(1);
      setHasMoreBrands(true);
      fetchBrands(1, brandSearch, false);
    }, 500);
    return () => clearTimeout(timer);
  }, [brandSearch]);

  /* ── Infinite scroll for brands ── */
  const handleBrandScroll = (e) => {
    const element = e.target;
    const bottom =
      element.scrollHeight - element.scrollTop <= element.clientHeight + 50;
    if (bottom && hasMoreBrands && !brandLoading) {
      const nextPage = brandPage + 1;
      setBrandPage(nextPage);
      fetchBrands(nextPage, brandSearch, true);
    }
  };

  /* ── Close sidebar on outside click ── */
  useEffect(() => {
    const h = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setSidebarOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  /* ── Fetch products (empty catid = no category filter = all products).
     Infinite scroll — same page-by-page + append pattern as All Medicines,
     so every matching product is reachable, not just the first 100. The
     search box hits the backend directly (search param) too, instead of
     only filtering whatever page of results happened to already be
     loaded. ── */
  const fetchProducts = async (page = 1, append = false) => {
    if (page === 1) setLoading(true);
    else setLoadingMore(true);
    try {
      const params = new URLSearchParams({ page, limit: 20, sort: sortOrder });
      // Always show in-stock products only — no toggle, always on.
      params.append("instock", "true");
      const normalizedSearch = normalizeSearchQuery(debouncedHeroSearch);
      if (normalizedSearch) params.append("search", normalizedSearch);
      const res = await axios.post(
        `${api_url}/category-manufacturer-products?${params.toString()}`,
        { category_ids: catid, manufacturer_ids: manufactid },
        { headers: { "Content-Type": "application/json", "x-auth": "false" } },
      );
      const data = res.data.data || [];
      const pagination = res.data.pagination || {};
      setTotalCount(pagination.total || data.length);
      setHasMore(page < (pagination.totalPages || 1));
      setProducts((prev) =>
        sortProductsByOrder(append ? [...prev, ...data] : data, sortOrder),
      );
    } catch (err) {
      console.error("Failed to fetch products:", err);
      if (!append) {
        setProducts([]);
        setTotalCount(0);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Initial fetch + reset on filter/sort/search change
  useEffect(() => {
    setCurrentPage(1);
    setProducts([]);
    setHasMore(true);
    fetchProducts(1, false);
  }, [catid, manufactid, sortOrder, debouncedHeroSearch]);

  // Intersection Observer for infinite scroll — same pattern as All Medicines
  useEffect(() => {
    const triggerElement = loadMoreTriggerRef.current;
    if (!triggerElement || !hasMore || loadingMore || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && hasMore && !loadingMore && !loading) {
          const nextPage = currentPage + 1;
          setCurrentPage(nextPage);
          fetchProducts(nextPage, true);
        }
      },
      { threshold: 0, rootMargin: "400px" },
    );

    observer.observe(triggerElement);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, currentPage, loading]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  /* ── Handlers ── */
  // Clicking a category here toggles it in/out of the selection (multi-select
  // stays available on this page's own sidebar) — at least one must remain
  // selected, so clicking the last remaining one does nothing.
  const HandlenewCategory = (id, name) => {
    const isActive = catid.includes(id);
    const newIds = isActive
      ? catid.length === 1
        ? catid
        : catid.filter((x) => x !== id)
      : [...catid, id];
    setCatid(newIds);
    const names = contextCategories
      .filter((c) => newIds.includes(c.category_id))
      .map((c) => c.category_name)
      .join(", ");
    setSelectedCategory(names || null);
    setSidebarOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const HandlenewManufact = (id, checked) => {
    setSelectedBrands((prev) => {
      const n = new Set(prev);
      checked ? n.add(id) : n.delete(id);
      return n;
    });
    setManufactid((prev) =>
      checked ? [...prev, id] : prev.filter((p) => p !== id),
    );
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleClearAll = () => {
    setSelectedBrands(new Set());
    setManufactid([]);
    setBrandSearch("");
  };

  const handleProductClick = (product_id, product) =>
    navigate(`/products/${toProductSlug(product)}`, {
      state: { product_id },
    });

  const OutOfQuantityCheck = (variants) =>
    variants.every((v) => !v.quantity || v.quantity === 0);
  const getCartQuantity = (variantId) => {
    const it = cartItems.find((ci) => ci.variant_id === variantId);
    return it ? it.quantity : 0;
  };

  const handleIncrease = async (e, variant, item) => {
    e.preventDefault();
    if (!variant?.variant_id) return;
    const qty = getCartQuantity(variant.variant_id);
    const res = await fetch(`${api_url}/cart`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
      },
      body: JSON.stringify({
        product_id: variant.product_id,
        quantity: qty + 1,
        variant_id: variant.variant_id,
      }),
    });
    const data = await res.json();
    if (
      !res.ok ||
      data.message?.toLowerCase().includes("stock not available")
    ) {
      toast.error(`${item.name} is out of stock`);
      return;
    }
    setCartItems((prev) =>
      prev.map((ci) =>
        ci.variant_id === variant.variant_id
          ? { ...ci, quantity: ci.quantity + 1 }
          : ci,
      ),
    );
  };

  const handleDecrease = async (e, variant) => {
    e.preventDefault();
    if (!variant?.variant_id) return;
    const qty = getCartQuantity(variant.variant_id);
    if (qty === 1) {
      await handleRemoveFromCart(variant.product_id, variant.variant_id);
      setCartItems((prev) =>
        prev.filter((ci) => ci.variant_id !== variant.variant_id),
      );
      return;
    }
    const res = await fetch(`${api_url}/cart`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
      },
      body: JSON.stringify({
        product_id: variant.product_id,
        quantity: qty - 1,
        variant_id: variant.variant_id,
      }),
    });
    const data = await res.json();
    if (
      !res.ok ||
      data.message?.toLowerCase().includes("stock not available")
    ) {
      toast.error("Something went wrong");
      return;
    }
    setCartItems((prev) =>
      prev.map((ci) =>
        ci.variant_id === variant.variant_id
          ? { ...ci, quantity: ci.quantity - 1 }
          : ci,
      ),
    );
  };

  // In-stock filtering and search now both happen on the backend (via the
  // `instock`/`search` params in fetchProducts), so totalCount already
  // reflects them.
  const displayCount = totalCount;

  /* ══════════════════════════════════════════════════════════════ */
  return (
    <div className="min-h-screen bg-[#f8fafa] font-poppins">
      <Helmet>
        <title>{selectedCategory || "Categories"} | MediSYS Pharmacy</title>
        <meta
          name="description"
          content="MediSYS Pharmacy - Buy medicines online and experience fastest home delivery."
        />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <HeroBannner
        breadcrumbs={[
          { label: "Home", to: "/home" },
          { label: "Medicines Categories" },
          ...(selectedCategory ? [{ label: selectedCategory }] : []),
        ]}
        title={selectedCategory || "All Categories"}
        subtitle={
          !loading
            ? `${displayCount} medicine${displayCount !== 1 ? "s" : ""} found`
            : "Loading products…"
        }
        badge="Browse"
        icon={<CategoryIcon />}
        accentColor="#5fbdb3"
      />

      {/* ── Search + Sort bar ── */}
      <div className="bg-white border-b border-[#c8d8d7] px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          {/* Search */}
          <div className="flex items-center gap-2.5 flex-1 bg-[#f2f8f7] border border-[#c8d8d7] focus-within:border-[#5fbdb3] focus-within:bg-white rounded-xl px-4 h-10 transition-all duration-200">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#5a7a77"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="flex-shrink-0"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              ref={searchInputRef}
              type="text"
              value={heroSearch}
              onChange={(e) => setHeroSearch(e.target.value)}
              placeholder="Search products…"
              className="flex-1 bg-transparent outline-none text-[13px] font-medium text-[#1e4a46] placeholder-[#5a7a77] font-poppins"
            />
            {heroSearch && (
              <button
                onClick={() => {
                  setHeroSearch("");
                  searchInputRef.current?.focus();
                }}
                className="text-[#5a7a77] hover:text-[#1e4a46] transition-colors"
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>
          {/* Right controls */}
          <div className="flex items-center flex-wrap gap-2 sm:gap-3">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden flex items-center gap-2 text-[13px] font-semibold text-[#1e4a46] border border-[#aac9c7] bg-[#ddf0ee] px-4 h-10 rounded-xl flex-shrink-0"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="4" y1="6" x2="20" y2="6" />
                <line x1="8" y1="12" x2="20" y2="12" />
                <line x1="12" y1="18" x2="20" y2="18" />
              </svg>
              Filter
              {selectedBrands.size > 0 && (
                <span className="bg-[#ff601e] text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">
                  {selectedBrands.size}
                </span>
              )}
            </button>
            <SortDropdown sortOrder={sortOrder} onChange={setSortOrder} />
            {!loading && (
              <div className="hidden sm:flex items-center gap-1.5 bg-[#ddf0ee] border border-[#aac9c7] rounded-xl px-3 h-10 flex-shrink-0">
                <span className="font-bold text-[13px] text-[#1e4a46]">
                  {displayCount}
                </span>
                <span className="text-[12px] text-[#5fbdb3] font-medium">
                  products
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row">
        {/* ── Sidebar ── */}
        <aside
          ref={dropdownRef}
          className={`${sidebarOpen ? "block" : "hidden"} lg:block lg:w-[260px] xl:w-[280px] flex-shrink-0 bg-white border-r border-[#c8d8d7]`}
        >
          <div className="lg:sticky lg:top-0 p-5 space-y-6 max-h-[70vh] lg:max-h-[calc(100vh-120px)] scrollbar-hide overflow-y-auto">
            {/* Categories */}
            <div>
              <div className="flex items-center justify-between mb-3 px-1">
                <h3 className="font-bold text-[14px] text-[#ff601e]">
                  Categories
                </h3>
                {catid.length > 1 && (
                  <button
                    onClick={() => {
                      const first = catid[0];
                      setCatid([first]);
                      // The breadcrumb/title track selectedCategory
                      // separately from catid — clearing has to update
                      // both, or the old joined names stick around.
                      setSelectedCategory(
                        contextCategories.find(
                          (c) => c.category_id === first,
                        )?.category_name || null,
                      );
                      // Any leftover brand search text should go too —
                      // otherwise it silently keeps narrowing the brand
                      // list after the rest of the filters were cleared.
                      setBrandSearch("");
                    }}
                    className="text-[11px] font-semibold text-[#ff601e] hover:underline"
                  >
                    Clear ({catid.length})
                  </button>
                )}
              </div>
              <div className="flex flex-col gap-1 max-h-[280px] sidebar-scroll overflow-y-auto custom-scrollbar pr-1">
                {contextCategories.map((cat) => {
                  const isActive = catid.includes(cat.category_id);
                  return (
                    <button
                      key={cat.category_id}
                      onClick={() =>
                        HandlenewCategory(cat.category_id, cat.category_name)
                      }
                      className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl w-full text-left transition-all duration-200 border cursor-pointer
                        ${isActive ? "bg-[#ddf0ee] border-[#aac9c7]" : "border-transparent hover:bg-[#f2f8f7] hover:border-[#c8d8d7]"}`}
                    >
                      <div
                        className={`w-4 h-4 rounded-md border-[1.5px] flex items-center justify-center flex-shrink-0 transition-all
                        ${isActive ? "bg-[#1e4a46] border-[#1e4a46]" : "border-[#aac9c7] bg-white"}`}
                      >
                        {isActive && (
                          <svg
                            width="9"
                            height="9"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="white"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        )}
                      </div>
                      <span
                        className={`text-[12px] leading-snug ${isActive ? "font-semibold text-[#1e4a46]" : "text-[#1e3330] font-medium"}`}
                      >
                        {cat.category_name}
                      </span>
                      {isActive && (
                        <span className="ml-auto w-1.5 h-1.5 rounded-full bg-[#ff601e] flex-shrink-0" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="h-px bg-[#dde9e8]" />

            {/* Brands */}
            <div>
              <div className="flex items-center justify-between mb-3 px-1">
                <h3 className="font-bold text-[14px] text-[#ff601e]">Brands</h3>
                {selectedBrands.size > 0 && (
                  <button
                    onClick={handleClearAll}
                    className="text-[11px] font-semibold text-[#ff601e] hover:underline cursor-pointer"
                  >
                    Clear ({selectedBrands.size})
                  </button>
                )}
              </div>
              {/* Brand search */}
              <div className="flex items-center gap-2 bg-[#f2f8f7] border border-[#c8d8d7] rounded-xl px-3 h-9 mb-3 focus-within:border-[#1e4a46] transition-all">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#5a7a77"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                  type="text"
                  placeholder="Search brands…"
                  value={brandSearch}
                  onChange={(e) => setBrandSearch(e.target.value)}
                  className="flex-1 bg-transparent outline-none text-[12px] text-[#1e3330] placeholder-[#5a7a77]"
                />
                {brandSearch && (
                  <button
                    type="button"
                    onClick={() => setBrandSearch("")}
                    className="text-[#5a7a77] hover:text-[#1e4a46] transition-colors flex-shrink-0 cursor-pointer"
                  >
                    <svg
                      width="11"
                      height="11"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                )}
              </div>
              {/* Brand list */}
              <div
                ref={brandScrollRef}
                onScroll={handleBrandScroll}
                className="flex flex-col gap-1 max-h-[260px] overflow-y-auto pr-1 scrollbar-hide"
              >
                {filteredBrands.map((brand) => {
                  const checked = selectedBrands.has(brand.id);
                  return (
                    <label
                      key={brand.id}
                      onClick={() => HandlenewManufact(brand.id, !checked)}
                      className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl cursor-pointer transition-all border
                        ${checked ? "bg-[#ddf0ee] border-[#aac9c7]" : "border-transparent hover:bg-[#f2f8f7] hover:border-[#c8d8d7]"}`}
                    >
                      <div
                        className={`w-4 h-4 rounded-md border-[1.5px] flex items-center justify-center flex-shrink-0 transition-all
                        ${checked ? "bg-[#1e4a46] border-[#1e4a46]" : "border-[#aac9c7] bg-white"}`}
                      >
                        {checked && (
                          <svg
                            width="9"
                            height="9"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="white"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        )}
                      </div>
                      <span
                        className={`text-[12px] ${checked ? "font-semibold text-[#1e4a46]" : "text-[#1e3330] font-medium"}`}
                      >
                        {brand.name}
                      </span>
                    </label>
                  );
                })}
                {filteredBrands.length === 0 && (
                  <div className="px-2 py-3">
                    <p className="text-[12px] text-[#5a7a77]">
                      No brands found.
                    </p>
                    {brandSearch && (
                      <button
                        onClick={() => setBrandSearch("")}
                        className="text-[11px] font-semibold text-[#ff601e] hover:underline mt-1 cursor-pointer"
                      >
                        Clear search
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </aside>

        {/* ── Products Grid ── */}
        <div className="flex-1 px-4 sm:px-6 lg:px-8 py-6">
          {loading && products.length === 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={i}
                  className="bg-white rounded-2xl border border-[#c8d8d7] overflow-hidden animate-pulse flex flex-col"
                >
                  <div className="h-[160px] bg-[#dde9e8]" />
                  <div className="p-3 flex flex-col gap-2">
                    <div className="h-3 bg-[#dde9e8] rounded-full w-3/4" />
                    <div className="h-3 bg-[#dde9e8] rounded-full w-1/2" />
                    <div className="h-9 bg-[#dde9e8] rounded-xl mt-1" />
                  </div>
                </div>
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <div className="w-16 h-16 rounded-2xl bg-[#ddf0ee] border border-[#aac9c7] flex items-center justify-center">
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#5fbdb3"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <path d="M16 10a4 4 0 0 1-8 0" />
                </svg>
              </div>
              <p className="font-semibold text-[15px] text-[#1e3330]">
                No medicines found
              </p>
              <p className="text-[13px] text-[#5a7a77]">
                Try changing your filters or selecting a different category.
              </p>
            </div>
          ) : (
            <div
              className={`grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 transition-opacity duration-200 ${loading ? "opacity-50 pointer-events-none" : "opacity-100"}`}
            >
              {products.map((product, index) => {
                const variants = product.variant || [];
                const { mrp, finalPrice, showDiscount, pct } = getPriceInfo(
                  variants,
                  product.discount_percent,
                );
                const isWishlisted = wishlistItems.some(
                  (w) =>
                    w.product_id === product.product_id &&
                    w.variant_id === product.variant?.[0]?.variant_id,
                );
                const isOutOfStock = OutOfQuantityCheck(variants);
                const cartQty = getCartQuantity(
                  product.variant?.[0]?.variant_id,
                );
                const isMultiVariant =
                  Array.isArray(product.variant) && product.variant.length > 1;
                const isOrange = index % 2 === 1;

                const cardBg = isOrange
                  ? "bg-gradient-to-b from-[#fff3ee] to-white"
                  : "bg-gradient-to-b from-[#eaf4f3] to-white";
                const cardBorder = isOrange
                  ? "border-[#ffd5c2] hover:border-[#ff601e]/60"
                  : "border-[#c8d8d7] hover:border-[#5fbdb3]";
                const accentBar = isOrange
                  ? "from-[#ff601e] to-[#e55519]"
                  : "from-[#1e4a46] via-[#5fbdb3] to-[#1e4a46]";
                const imgBg = isOrange ? "bg-[#fff8f5]" : "bg-[#f4fbfa]";
                const shadow = isOrange
                  ? "shadow-[0_2px_12px_rgba(255,96,30,0.09)] hover:shadow-[0_10px_28px_rgba(255,96,30,0.22)]"
                  : "shadow-[0_2px_12px_rgba(95,189,179,0.09)] hover:shadow-[0_10px_28px_rgba(95,189,179,0.22)]";
                const btnCls = isOrange
                  ? "bg-[#ff601e] hover:bg-[#e55519] shadow-[0_2px_8px_rgba(255,96,30,0.3)]"
                  : "bg-[#1e4a46] hover:bg-[#ff601e] shadow-[0_2px_8px_rgba(22,44,42,0.2)] hover:shadow-[0_4px_14px_rgba(255,96,30,0.4)]";
                const discCls = isOrange ? "text-[#ff601e]" : "text-[#5fbdb3]";

                return (
                  <div
                    key={product.product_id}
                    className={`group border-[1.5px] ${cardBorder} ${cardBg} ${shadow} rounded-2xl overflow-hidden flex flex-col relative hover:-translate-y-2 transition-all duration-300 cursor-pointer`}
                    style={{
                      animation: `fadeInUp 0.35s ease ${(index % 8) * 40}ms both`,
                    }}
                  >
                    <div
                      className={`absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r ${accentBar} opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10`}
                    />

                    {/* Discount badge — only when real discount */}
                    {showDiscount && pct > 0 && (
                      <div className="absolute top-3 right-3 z-10 bg-[#ff601e] text-white text-[9px] sm:text-[10px] font-bold w-9 h-9 rounded-full flex flex-col items-center justify-center shadow-[0_2px_8px_rgba(255,96,30,0.45)] leading-tight text-center">
                        {pct}%<br />
                        OFF
                      </div>
                    )}

                    {/* Wishlist */}
                    <button
                      className="absolute top-3 left-3 z-10 w-7 h-7 sm:w-8 sm:h-8 bg-white rounded-xl border border-[#c8d8d7] flex items-center justify-center shadow-sm hover:border-[#5fbdb3] hover:bg-[#ddf0ee] transition-all duration-200"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!localStorage.getItem("authToken")) {
                          setShowLoginPopup(true);
                          return;
                        }
                        isWishlisted
                          ? removeFromWishlist(
                              product.product_id,
                              product.variant?.[0]?.variant_id,
                            )
                          : addToWishlist(product, product.variant?.[0]);
                      }}
                      disabled={wishlistLoading}
                      onMouseEnter={() => setTooltipOpen(index)}
                      onMouseLeave={() => setTooltipOpen(null)}
                    >
                      <img
                        src={isWishlisted ? wish : wishlist}
                        alt="wishlist"
                        className="w-3.5 h-3.5 sm:w-4 sm:h-4"
                      />
                      {tooltipOpen === index && (
                        <div className="absolute left-9 top-0 bg-[#162c2a] text-white text-[10px] font-medium px-2 py-1 rounded-lg whitespace-nowrap shadow z-30">
                          {isWishlisted
                            ? "Remove from wishlist"
                            : "Add to wishlist"}
                        </div>
                      )}
                    </button>

                    {/* Image */}
                    <div
                      className={`w-full h-full ${imgBg} overflow-hidden cursor-pointer group`}
                      onClick={() =>
                        handleProductClick(product.product_id, product)
                      }
                    >
                      <img
                        src={
                          product?.product_images?.image_url?.primary_image ||
                          "/default.svg"
                        }
                        alt={product.name || "Product"}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "/default.svg";
                        }}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                      />
                    </div>

                    <div className="w-full h-px bg-[#dde9e8]" />

                    {/* Info */}
                    <div
                      className="px-3 sm:px-4 pt-3 pb-2 flex-1 flex flex-col gap-1.5 cursor-pointer"
                      onClick={() =>
                        handleProductClick(product.product_id, product)
                      }
                    >
                      <h3 className="font-semibold text-[12px] sm:text-[13px] text-[#1e4a46] line-clamp-2 leading-snug group-hover:text-[#ff601e] transition-colors duration-200">
                        {product.name || "Unnamed Product"}
                      </h3>
                      {/* Price — correct mrp mapping */}
                      <div className="flex items-baseline gap-2 flex-wrap">
                        {mrp ? (
                          <>
                            {showDiscount && (
                              <span className="text-[11px] text-[#7a9b98] line-through">
                                ₹{mrp}
                              </span>
                            )}
                            <span className="font-bold text-[14px] sm:text-[15px] text-[#1e4a46]">
                              ₹{finalPrice}
                            </span>
                            {showDiscount && pct > 0 && (
                              <span
                                className={`text-[10px] font-semibold ${discCls}`}
                              >
                                {pct}% off
                              </span>
                            )}
                          </>
                        ) : (
                          <span className="text-[12px] text-[#5a7a77] font-medium">
                            Price N/A
                          </span>
                        )}
                      </div>
                      {isOutOfStock && (
                        <span className="self-start text-[10px] font-semibold text-red-500 bg-red-50 border border-red-100 rounded-lg px-2 py-0.5">
                          Out of Stock
                        </span>
                      )}
                    </div>

                    {/* Cart action */}
                    <div className="px-3 sm:px-4 pb-4 pt-1">
                      {isMultiVariant ? (
                        <button
                          className={`w-full h-10 ${btnCls} disabled:bg-[#c5d5d4] text-white text-[11px] sm:text-[12px] font-semibold rounded-xl flex items-center justify-center gap-1.5 transition-all duration-200`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!localStorage.getItem("authToken")) {
                              setShowLoginPopup(true);
                              return;
                            }
                            handleProductClick(product.product_id, product);
                          }}
                        >
                          <img
                            src={addToCartIcon}
                            alt="Cart"
                            className="w-3.5 h-3.5 object-contain"
                          />
                          Choose Variant
                        </button>
                      ) : cartQty > 0 ? (
                        <div className="flex items-center justify-between bg-[#ddf0ee] border border-[#aac9c7] rounded-xl h-10 px-3">
                          <button
                            onClick={(e) =>
                              handleDecrease(e, product.variant[0], product)
                            }
                            className="w-7 h-7 flex items-center justify-center text-[#1e4a46] font-bold text-xl hover:bg-white rounded-lg transition-colors"
                          >
                            −
                          </button>
                          <span className="font-bold text-[14px] text-[#1e4a46] min-w-[24px] text-center">
                            {cartQty}
                          </span>
                          <button
                            onClick={(e) =>
                              handleIncrease(e, product.variant[0], product)
                            }
                            className="w-7 h-7 flex items-center justify-center text-[#1e4a46] font-bold text-xl hover:bg-white rounded-lg transition-colors"
                          >
                            +
                          </button>
                        </div>
                      ) : (
                        <button
                          className={`w-full h-10 ${btnCls} disabled:bg-[#c5d5d4] disabled:cursor-not-allowed text-white text-[11px] sm:text-[13px] font-semibold rounded-xl flex items-center justify-center gap-1.5 transition-all duration-200`}
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (!localStorage.getItem("authToken")) {
                              setShowLoginPopup(true);
                              return;
                            }
                            await handleAddToCart(
                              product,
                              product.variant[0],
                              1,
                            );
                          }}
                          disabled={isOutOfStock}
                        >
                          <img
                            src={addToCartIcon}
                            alt="Cart"
                            className="w-3.5 h-3.5 object-contain"
                          />
                          {isOutOfStock ? "Out of Stock" : "Add to Cart"}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {products.length > 0 && (
            <div
              ref={loadMoreTriggerRef}
              className="flex justify-center items-center py-8 min-h-[100px]"
            >
              {loadingMore && (
                <div className="flex items-center gap-2 text-[#5fbdb3] text-[13px] font-medium">
                  <svg
                    className="animate-spin h-5 w-5"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Loading more medicines…
                </div>
              )}
              {!hasMore && !loading && (
                <div className="text-[13px] text-[#8faba9] font-medium">
                  You've reached the end of the list
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showLoginPopup && <Popup onClose={() => setShowLoginPopup(false)} />}
      <style>{`
        @keyframes fadeInUp { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
        .scrollbar-hide::-webkit-scrollbar { display:none; }
        .scrollbar-hide { -ms-overflow-style:none; scrollbar-width:none; }
      `}</style>
    </div>
  );
};

export default Categories;
